import { BoardToolbar, ChartGrid, ScrapeNotice } from '../components/ChartBoard';
import { useChartBoard } from '../lib/useChartBoard';

/**
 * Live vitals: what both systems are doing right now and were doing an hour
 * ago, read from the Prometheus already running on the VPS.
 *
 * sphere-watch stores no history of its own — that instance already has it, and
 * already scrapes both monitored apps, whose prom-client defaults carry heap,
 * event-loop lag, CPU and RSS for free.
 *
 * This is not trying to replace Grafana, and the link out says so. Grafana is
 * where you build; this is where you look while reading an incident, so the
 * chart that explains it is on the same screen as the thing that fired — with
 * the incident drawn on it. The panels are therefore a fixed, curated set
 * rather than a query builder, and that is also what keeps the endpoint an
 * allowlist instead of an SSRF-shaped PromQL proxy into an internal Prometheus.
 * What IS Grafana-shaped is the handling of time: one window for the whole
 * screen, in the URL, with drag-to-zoom, pan, zoom-out, a custom picker and
 * auto-refresh — because reading a spike means moving around it.
 *
 * The seven per-tenant panels are NOT here; they are under the Tenants table,
 * next to the rows they describe. GROUPS is therefore also the fetch list —
 * `useChartBoard` loads exactly the keys it is given, and
 * `scripts/verify/charts.js` asserts every allowlisted panel is drawn on
 * exactly one screen, because one fetched here and drawn there would be a range
 * query against a shared Prometheus on every window change, forever, for
 * nothing.
 */
const GROUPS: [string, string[]][] = [
  ['App boxes', ['node_cpu', 'node_memory']],
  ['Compute', ['cpu_process', 'cpu_host', 'memory_rss', 'memory_host']],
  ['Node runtime', ['heap', 'heap_total', 'event_loop', 'handles']],
  ['Databases', ['db_connections', 'db_cache_hit', 'db_txn', 'db_size']],
  ['Queues and Redis', ['queue_waiting', 'queue_failed']],
  ['sphere-watch itself', ['incidents', 'check_age']],
];

const KEYS = GROUPS.flatMap(([, keys]) => keys);

export function Vitals() {
  const board = useChartBoard(KEYS);
  const { meta, error, range, win } = board;

  if (error) return <div className="wt-banner wt-banner-danger">{error.message}</div>;
  if (!meta || !range || !win) return <div className="wt-empty">Loading…</div>;

  return (
    <>
      <div className="wt-head">
        <h1>Vitals</h1>
        <span className="sub">from the Prometheus on the VPS</span>
      </div>

      <BoardToolbar board={board} />
      <ScrapeNotice board={board} />

      {!meta.available && (
        <div className="wt-banner wt-banner-warn">
          <strong>WATCH_PROMETHEUS_URL is not set</strong>, so there is no history to draw. sphere-watch keeps
          no time series of its own — point this at the Prometheus already running on the VPS and these fill
          in.
        </div>
      )}

      {GROUPS.map(([group, keys]) => (
        <section key={group} style={{ marginBottom: 26 }}>
          <div className="wt-daygroup" style={{ marginTop: 0 }}>
            {group}
          </div>
          <ChartGrid board={board} keys={keys} />
        </section>
      ))}

      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        {/* Said plainly rather than left for someone to discover. */}
        Host CPU, freeable memory, IOPS and read/write latency for an RDS instance are hypervisor-level and
        live in CloudWatch — nothing inside Postgres can see them, so they are not here. What the database
        knows about itself is. Per-tenant activity is on the Tenants screen, beside the rows it describes.
      </p>
    </>
  );
}
